import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';

import { UsersService } from '../http-services/users.service';
import { AuthService } from './auth.service';

import { APP_TOKEN, AppsEnum } from '@ose/enums';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  readonly languages = ['fa', 'en'];

  currentLang?: string;

  private readonly usersService = inject(UsersService);
  private readonly authService = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly cookieService = inject(CookieService);
  private readonly app = inject(APP_TOKEN);
  private readonly doc = inject(DOCUMENT);

  private readonly direction$ = new BehaviorSubject<'rtl' | 'ltr'>('ltr');
  private readonly currentLang$ = new BehaviorSubject<string>('en');

  constructor() {
    this.translate.addLangs(this.languages);

    const defaultLanguage = this.getDefaultLanguage();

    this.translate.setDefaultLang(defaultLanguage);

    this.initializeLanguage();
  }
  private getDefaultLanguage(): string {
    if (this.app === AppsEnum.SUPPORT || this.app === AppsEnum.CLIENT) {
      return 'fa';
    }

    return 'en';
  }

  private initializeLanguage(): void {
    const externalLanguage = this.getExternalLanguage();

    if (externalLanguage) {
      this.setCurrentLanguage(externalLanguage, false);
      return;
    }
    const cookieLanguage = this.normalizeLanguage(
      this.cookieService.get('language')
    );

    if (cookieLanguage) {
      this.setCurrentLanguage(cookieLanguage, false);
      return;
    }
    if (this.app === AppsEnum.SUPPORT || this.app === AppsEnum.CLIENT) {
      this.setCurrentLanguage('fa', false);
      return;
    }
    const navigatorInstance = this.doc.defaultView?.navigator;

    const browserLanguages = navigatorInstance?.languages?.length
      ? navigatorInstance.languages
      : [navigatorInstance?.language ?? this.getDefaultLanguage()];

    const matchedLanguage = browserLanguages
      .map((language) => this.normalizeLanguage(language))
      .find((language): language is string => Boolean(language));

    this.setCurrentLanguage(
      matchedLanguage ?? this.getDefaultLanguage(),
      false
    );
  }

  private getExternalLanguage(): string | null {
    return this.getLanguageFromUrl(this.doc.location.href);
  }

  syncLanguageFromUrl(url: string, persistOnServer = true): void {
    const language = this.getLanguageFromUrl(url);

    if (!language) {
      return;
    }

    this.setCurrentLanguage(language, persistOnServer);
  }

  setCurrentLanguage(language: string, persistOnServer = true): void {
    const normalizedLanguage = this.normalizeLanguage(language);

    if (!normalizedLanguage) {
      return;
    }

    const languageChanged = this.currentLang !== normalizedLanguage;

    if (!languageChanged) {
      return;
    }

    this.applyLanguage(normalizedLanguage);

    if (persistOnServer) {
      void this.persistLanguage(normalizedLanguage);
    }
  }

  private normalizeLanguage(
    language: string | null | undefined
  ): string | null {
    if (!language) {
      return null;
    }

    const normalizedLanguage = language.trim().toLowerCase().split('-')[0];

    return this.languages.includes(normalizedLanguage)
      ? normalizedLanguage
      : null;
  }

  private getLanguageFromUrl(url: string): string | null {
    try {
      const parsedUrl = new URL(url, this.doc.location.origin);

      return this.normalizeLanguage(parsedUrl.searchParams.get('lang'));
    } catch {
      return null;
    }
  }

  private applyLanguage(language: string): void {
    const isRtl = language === 'fa' || language === 'ar';
    this.currentLang = language;
    this.translate.use(language);
    this.currentLang$.next(language);
    this.doc.documentElement.lang = language;
    this.doc.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    this.direction$.next(isRtl ? 'rtl' : 'ltr');
    this.setLanguageCookie(language);
    this.updateBootstrapStyle(isRtl);
    this.updateFontStyle(language, isRtl);
    this.updateCrisp(language, isRtl);
  }
  private setLanguageCookie(language: string): void {
    const hostname = this.doc.location.hostname;

    const expires = new Date();

    expires.setFullYear(expires.getFullYear() + 1);

    if (hostname.includes('onesecondexchange')) {
      const domainParts = hostname.split('.');

      const rootDomain =
        domainParts.length > 2 ? domainParts.slice(-2).join('.') : hostname;

      this.cookieService.set(
        'language',
        language,
        expires,
        '/',
        `.${rootDomain}`
      );

      return;
    }

    this.cookieService.set('language', language, expires, '/');
  }

  private updateBootstrapStyle(isRtl: boolean): void {
    const customBootstrap = this.doc.getElementById(
      'customBsStyle'
    ) as HTMLLinkElement | null;

    if (!customBootstrap) {
      return;
    }

    customBootstrap.href = isRtl
      ? '/assets/styles/custom-bootstrap.rtl.min.css'
      : '/assets/styles/custom-bootstrap.min.css';
  }

  private updateFontStyle(language: string, isRtl: boolean): void {
    const fontFace = this.doc.getElementById(
      'fontFace'
    ) as HTMLLinkElement | null;

    if (!fontFace) {
      return;
    }

    fontFace.href = isRtl
      ? `/assets/styles/font-${language}.css`
      : '/assets/styles/font.css';
  }

  private updateCrisp(language: string, isRtl: boolean): void {
    const crisp = (
      this.doc.defaultView as
        | (Window & {
            $crisp?: unknown[];
          })
        | null
    )?.$crisp;

    if (!crisp) {
      return;
    }

    crisp.push(['config', 'position:reverse', isRtl]);

    crisp.push(['set', 'session:language', language]);
  }

  private async persistLanguage(language: string): Promise<void> {
    if (!this.authService.isAuthenticated || this.app === AppsEnum.SUPPORT) {
      return;
    }

    try {
      const currentUser =
        this.usersService.currentUser ??
        (await firstValueFrom(this.usersService.loadCurrentUser()));

      if (!currentUser) {
        return;
      }

      const currentUserLanguage = this.getUserLanguage(currentUser);

      if (currentUserLanguage === language) {
        return;
      }

      await firstValueFrom(
        this.usersService.updateCurrentUserLanguage(language)
      );
    } catch (error) {
      console.error('Error updating user language:', error);
    }
  }

  private getUserLanguage(user: unknown): string | undefined {
    if (!user || typeof user !== 'object') {
      return undefined;
    }

    if (!('language' in user)) {
      return undefined;
    }

    const language = user.language;

    return typeof language === 'string' ? language : undefined;
  }

  getCurrentDirection(): Observable<'rtl' | 'ltr'> {
    return this.direction$.asObservable();
  }

  getCurrentLanguage(): Observable<string> {
    return this.currentLang$.asObservable();
  }
}
